import cn from 'classnames';
import { InputHTMLAttributes } from 'react';

export interface RadioOption<T> {
  label: string;
  value: T;
  disabled?: boolean;
}

export interface RadioInputProps<T> extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  additionalClass?: string;
  formCheckAdditionalClass?: string;
  isSmallSize?: boolean;
  direction?: 'horizontal' | 'vertical';
  options: RadioOption<T>[];
  value?: T;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: T) => void;
  valueToString?: (value: T) => string;
  stringToValue?: (str: string) => T;
}

export const RadioInput = <T,>({
  additionalClass = '',
  formCheckAdditionalClass = '',
  isSmallSize = false,
  direction = 'horizontal',
  options,
  name,
  value,
  onChange,
  valueToString,
  stringToValue,
  ...rest
}: RadioInputProps<T>) => {
  const firstOptionValue = options[0]?.value;

  const getValueToString =
    valueToString ||
    ((val: T): string => {
      if (typeof val === 'boolean') {
        return val ? 'true' : 'false';
      }
      if (typeof val === 'number') {
        return val.toString();
      }
      return String(val);
    });

  const getStringToValue =
    stringToValue ||
    ((str: string): T => {
      if (typeof firstOptionValue === 'boolean') {
        return (str === 'true') as T;
      }
      if (typeof firstOptionValue === 'number') {
        return Number(str) as T;
      }
      return str as T;
    });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = getStringToValue(event.target.value);

    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: selectedValue,
        name: event.target.name
      }
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent, selectedValue);
  };

  const isOptionChecked = (optionValue: T): boolean => {
    if (value === undefined) return false;
    return optionValue === value;
  };

  return (
    <div className={cn('d-flex', additionalClass)}>
      <div
        className={cn('d-flex gap-3 w-100', {
          'flex-column': direction === 'vertical',
          'align-items-center': direction === 'horizontal'
        })}
      >
        {options.map((option, index) => {
          const optionValue = getValueToString(option.value);
          const optionId = `${name}-${optionValue}-${index}`;
          const isChecked = isOptionChecked(option.value);

          return (
            <div key={optionId} className={cn('form-check', formCheckAdditionalClass)}>
              <input
                type="radio"
                value={optionValue}
                id={optionId}
                name={name}
                checked={isChecked}
                onChange={handleChange}
                disabled={option.disabled || rest.disabled}
                className={cn('form-check-input', {
                  'form-check-input-sm': isSmallSize
                })}
                {...rest}
              />
              <label htmlFor={optionId} className="form-check-label">
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};
